// Type
import { PointOptionsObject } from "highcharts";
import { OptionsBubbleSeries } from "store/slice/charts";
import { BalanceSerie } from "store/slice/charts/chartsSlice.types";

type TSelectedSerie = {
    name: string;
    isCompany: boolean;
    index: number;
};
type UpdateSeriesWrapper = (
    isLegendClicked: React.MutableRefObject<boolean>,
    setChartOptions: React.Dispatch<React.SetStateAction<OptionsBubbleSeries>>,
    visibleSeries: React.MutableRefObject<number[]>
) => (chart: Highcharts.Chart) => void;

const generatePointInfo = (
    pointInfo: Highcharts.PointOptionsObject,
    dataLabelEnabled: boolean,
    markerEnabled: boolean
): Highcharts.PointOptionsObject => ({
    ...pointInfo,
    marker: { ...pointInfo.marker, enabled: markerEnabled },
    dataLabels: { ...pointInfo.dataLabels, enabled: dataLabelEnabled },
});

export const updateSeriesWrapper: UpdateSeriesWrapper = (isLegendClicked, setChartOptions, visibleSeries) => {
    const updateSeries = (chart: Highcharts.Chart) => {
        if (!isLegendClicked.current) return;

        const seriesRef: Highcharts.Series[] = chart.series;
        const selectedSeries: TSelectedSerie[] = seriesRef
            .filter(({ visible }) => visible)
            .map(({ name, index, options }) => ({ name, isCompany: options?.custom ? true : false, index }));
        const selectedSeriesId = selectedSeries.map(({ index }) => index);
        const companiesSelected = selectedSeries.filter(({ isCompany }) => isCompany).map(({ name }) => name);
        const rolesSelected = selectedSeries.filter(({ isCompany }) => !isCompany).map(({ name }) => name);

        isLegendClicked.current = false;

        setChartOptions(({ series, ...restOptions }) => {
            let newSeries: BalanceSerie[] = [];
            const generateData = (pointInfo: PointOptionsObject) => {
                const hasRole = rolesSelected.includes(pointInfo.name as string);

                // Show/hide the points of the company serie if the point is the role selected
                return hasRole ? generatePointInfo(pointInfo, true, true) : generatePointInfo(pointInfo, false, false);
            };

            // If no companies selected but there are roles selected, select all companies
            if (companiesSelected.length === 0 && rolesSelected.length) {
                newSeries =
                    series?.map((serie, index) => {
                        const isCompany = serie.kind === "real" && !!serie?.custom;
                        if (isCompany) visibleSeries.current.push(index);

                        return isCompany && serie.kind === "real"
                            ? {
                                  ...serie,
                                  data: (serie.data as Highcharts.PointOptionsObject[])?.map(generateData),
                                  visible: true,
                              }
                            : { ...serie, visible: seriesRef[index].visible ? true : false };
                    }) || [];
            } else {
                newSeries =
                    series?.map((serie, index) => {
                        const isSelected = selectedSeriesId.includes(index);
                        const isCompany = serie.kind === "real" && !!serie?.custom;

                        if (isSelected && isCompany && serie.kind === "real") {
                            // If the company has roles, show only these points
                            if (rolesSelected.length) {
                                const newData = (serie.data as Highcharts.PointOptionsObject[])?.map(generateData);
                                return { ...serie, data: newData, visible: isSelected };
                            } else {
                                //if not roles selected, show all points of the company serie
                                const newData =
                                    (serie.data as Highcharts.PointOptionsObject[])?.map((pointInfo) =>
                                        generatePointInfo(pointInfo, true, true)
                                    ) || [];

                                return { ...serie, data: newData, visible: isSelected, type: "bubble" };
                            }
                        } else return { ...serie, visible: isSelected, type: "bubble" };
                    }) || [];
            }

            return { ...restOptions, series: newSeries };
        });
    };

    return updateSeries;
};

// interface ArchetypeLocationElement {
//     config: any;
//     svg: Highcharts.SVGElement;
// }
// interface ArchetypeLocation {
//     g: ArchetypeLocationElement;
//     circle: ArchetypeLocationElement;
//     label: ArchetypeLocationElement;
// }

// const archetypeLocations: ArchetypeLocation[] = [];

// export const drawWatermarks = (
//     chart: Highcharts.Chart,
//     chartData: LD5Chart,
//     moreOptions?: TMoreOptions,
//     isExporting?: boolean
// ) => {
//     if (archetypeLocations.length) {
//         archetypeLocations.forEach((el) => {
//             Object.values(el).forEach((el) => el.svg.destroy());
//         });
//     }

//     const renderer = chart.renderer as Highcharts.SVGRenderer;
//     const radius = Math.min(chart.chartWidth, chart.chartHeight) * RADIUS_PROPORTION;

//     chartData.watermarks.forEach((data, i) => {
//         archetypeLocations[i] = {
//             g: {} as ArchetypeLocationElement,
//             circle: {} as ArchetypeLocationElement,
//             label: {} as ArchetypeLocationElement,
//         };

//         // Group
//         const groupConfig = {
//             args: `archetype-${i}`,
//             translate: [chart.xAxis[0].toPixels(data.x, true), chart.yAxis[0].toPixels(data.y, true)],
//         };
//         archetypeLocations[i].g = {
//             config: groupConfig,
//             svg: renderer
//                 .g(groupConfig.args)
//                 .translate(...(groupConfig.translate as [number, number]))
//                 .add(),
//         };

//         // Circle
//         const circleConfig = {
//             args: [0, 0, radius],
//             attr: { ...CIRCLE_STYLES },
//         };
//         archetypeLocations[i].circle = {
//             config: circleConfig,
//             svg: renderer
//                 .circle(...circleConfig.args)
//                 .attr(circleConfig.attr)
//                 .add(archetypeLocations[i].g.svg),
//         };

//         // Label
//         const labelConfig = {
//             args: [data.label, 0, 0],
//             attr: { zIndex: 4 },
//             css: LABEL_STYLES,
//         };
//         archetypeLocations[i].label = {
//             config: labelConfig,
//             svg: renderer
//                 .label(data.label, 0, 0)
//                 .attr(labelConfig.attr)
//                 .css(labelConfig.css)
//                 .add(archetypeLocations[i].g.svg),
//         };

//         // Adjust label position
//         archetypeLocations[i].label.svg.attr({
//             x:
//                 archetypeLocations[i].circle.svg.getBBox().x +
//                 archetypeLocations[i].circle.svg.getBBox().width / 2 -
//                 archetypeLocations[i].label.svg.getBBox().width / 2,
//             y:
//                 archetypeLocations[i].circle.svg.getBBox().y +
//                 archetypeLocations[i].circle.svg.getBBox().height / 2 -
//                 archetypeLocations[i].label.svg.getBBox().height / 2 -
//                 1,
//         });

//         // if (isExporting) {
//         //     archetypeLocations.forEach((archetype) => {
//         //         const group = archetype.g.svg;
//         //         Object.entries(archetype).forEach((el) => {
//         //             if (el[0] === "g") {
//         //                 createHighchartsSvg(chart.renderer, el[1].config, el[0] as any);
//         //             } else {
//         //                 createHighchartsSvg(chart.renderer, el[1].config, el[0] as any, group);
//         //             }
//         //         });
//         //     });
//         // }
//     });
// };
